import React from 'react';
// template components
import Accordion from '@nmx/template/dist/componentLibrary/modules/Accordion';
import BulletedList from '@nmx/template/dist/componentLibrary/components/BulletedList';
import Col from '@nmx/template/dist/componentLibrary/foundations/Grid/Col';
import Link from '@nmx/template/dist/componentLibrary/components/Link';
import Row from '@nmx/template/dist/componentLibrary/foundations/Grid/Row';
import SectionContainer from '@nmx/template/dist/componentLibrary/components/SectionContainer';
import Typography from '@nmx/template/dist/componentLibrary/foundations/Typography';
// icons
import MinusSignFillIcon from '@nmx/template/dist/componentLibrary/foundations/Icon/icons/utility/MinusSignFill';
import PlusSignFillIcon from '@nmx/template/dist/componentLibrary/foundations/Icon/icons/utility/PlusSignFill';
// native components
import ClientServicesPhone4644, { clientServicesPhoneNumberLinkUrl } from '../../../../components/clientServicesPhone4644/clientServicesPhone4644';
// styles
import {
  TDStyled,
  TDStyledBold,
  TStyledBorder,
  TStyledHead,
} from './styles';

const bulletedListConfig = [
  { bulletText: (<>Marketing and advertising of our company and its products and services: information collected to offer NM products and services, personalize an individual&#39;s NM website or mobile app experience, and to deliver content or product and service offerings relevant to an individual&#39;s interests, including targeted ads and promotional offers.</>) },
  { bulletText: (<>Research &#38; strategic development: information collected for business, product, strategy, and technological development, excluding marketing and advertising activities.</>) },
  { bulletText: (<>Legal &#47; Compliance &#47; Regulatory purposes: information collected to meet internal and regulatory compliance requirements, respond to regulatory exams, conduct internal and external audits, respond to subpoenas and other law enforcement requests, record keeping, and enforce or defend our rights and property.</>) },
  { bulletText: (<>Online auditing: auditing related to an interaction with a consumer and a concurrent transaction, including, but not limited to, counting ad impressions to unique visitors, verifying positioning and quality of ad impressions, and auditing compliance with standards that may apply.</>) },
  { bulletText: (<>Detecting and protecting: detecting and protecting against security incidents and malicious, deceptive, fraudulent, or illegal activity.</>) },
  { bulletText: (<>Debugging: activities to identify and repair errors in technological functionality.</>) },
  { bulletText: (<>Backups and archives: data that is kept to ensure business continuity, for historical reference, and to meet record&#45;keeping obligations.</>) },
  { bulletText: (<>Analytic activities: analysis of data to improve NM marketing, advertising, websites, mobile app, products, and services&#59; to complete behavioral research&#59; to do other scientific research, reporting, or evaluation. Further, to enable us to continually improve on the content we offer you, as well as for purposes of personalization, analyzing user behavior, enhancing navigation, systems administration, site security, and fraud detection and prevention we capture IP addresses and track your activity on our websites.</>) },
  { bulletText: (<>NM&#45;provided products and services: issuing and servicing of NM products and services, including but not limited to: customer service&#59; verifying consumer information&#59; responding to consumer requests&#59; illustrations&#59; applications&#59; underwriting&#59; issuance&#59; transactions&#59; claims processing&#59; and account maintenance related to NM&#39;s insurance products, investment products and services, and financial planning.</>) },
  { bulletText: (<>Short&#45;term, transient use: data collected, but not retained, other than to facilitate a one&#45;time transaction or non&#45;personalized advertising shown as part of a consumer&#39;s current interaction with the business when information is not disclosed to another third party or used to build a profile or to alter consumer experience outside of with the current interaction with the business.</>) },
  { bulletText: (<>For additional purposes that may be described to you at the time of collection.</>) },
];

const contactInfoBulletedListConfig = [
  {
    bulletText: (
      <>
        Completing the&nbsp;
        <Link
          id='ccpa-consumer-contact-info-link'
          variant='primaryDeemphasize'
          linkUrl='https://privacyportal-na01.onetrust.com/webform/ba631c60-594d-44e1-9224-aeb0fabbe5ac/122edc91-0776-4739-a150-fa1588223227/'
          ariaLabel='See our consumer rights request form'
          isOutgoingLink>
          California Consumer Right to Access, Correct, or Delete Form
        </Link>
      </>
    ),
  },
  {
    bulletText: (
      <>
        Calling toll&#45;free at{' '}
        <Link
          id='ccpa-phone-number-link'
          variant='primaryDeemphasize'
          linkUrl={clientServicesPhoneNumberLinkUrl}>
          <ClientServicesPhone4644 />
        </Link>
        &#59; when prompted say &#34;California Consumer Privacy Act,&#34; &#34;CCPA,&#34; &#34;California Privacy Rights Act,&#34; or &#34;CPRA&#34;
      </>
    ),
  },
  {
    bulletText: (
      <>
        Emailing us at:&nbsp;
        <Link
          id='ccpa-notice-at-collection-customer-service-email-link'
          variant='primaryDeemphasize'
          linkUrl='mailto:customerservice8200@northwesternmutual.com'>
          customerservice8200&#64;northwesternmutual.com
        </Link>
      </>
    ),
  },
];

const privacyPoliciesBulletedListConfig = [
  {
    bulletText: (
      <Link
        id='ccpa-consumer-privacy-policy-link'
        variant='primaryDeemphasize'
        linkUrl='/ccpa-privacy-policy/'>
        California Consumer Privacy Policy
      </Link>
    ),
  },
  {
    bulletText: (
      <Link
        id='ccpa-workforce-privacy-policy-link'
        variant='primaryDeemphasize'
        linkUrl='/california-workforce-policy-notice/'>
        California Workforce Privacy Policy
      </Link>
    ),
  },
  {
    bulletText: (
      <Link
        id='ccpa-sales-force-privacy-policy-link'
        variant='primaryDeemphasize'
        linkUrl='/california-agents-policy-notice/'>
        California Sales Force Privacy Policy
      </Link>
    ),
  },
];

export default () => (
  <SectionContainer id='ccpa-notice-at-collection'>
    <Row align='center'>
      <Col medium={8}>
        <Typography
          nextElement='typography'
          component='h1'>
          California Notice at Collection
        </Typography>
        <Typography
          nextElement='typography'
          component='span'
          italic
          weight={500}>
          Last Updated: <time dateTime='2023-05-26'>May 26, 2023</time>
        </Typography>
        <Typography nextElement='typography'>
          <Link
            id='ccpa-notice-at-collection-printable-link'
            variant='primaryDeemphasize'
            linkUrl='/assets/pdf/legal/ccpa-notice-at-collection.pdf'
            isInternalPdf
            aria-label='Printable Version: CCPA Notice at Collection'>
            Printable Version
          </Link>
        </Typography>
        <Typography
          nextElement='typography'
          component='h2'
          variant='h3'>
          Who We Are
        </Typography>
        <Typography nextElement='typography'>
          References to the &#34;Northwestern Mutual Family of Companies,&#34; &#34;Northwestern Mutual,&#34; &#34;NM,&#34; &#34;we,&#34; &#34;us,&#34; &#34;our,&#34; or &#34;ours&#34; within this Notice mean The Northwestern Mutual Life Insurance Company, Northwestern Long
          Term Care Company, Northwestern Mutual Investment Services Company, Northwestern Wealth Management Company,
          Northwestern Mutual Investment Management Company, and the network offices of our sales force. Our sales
          force includes the agents who sell our products and services, along with their staffs.
        </Typography>
        <Typography
          nextElement='typography'
          component='h2'
          variant='h3'>
          Defined Terms
        </Typography>
        <Typography nextElement='typography'>
          For purposes of this Notice, the terms &#34;Personal Information,&#34; &#34;Notice at Collection,&#34; &#34;consumer,&#34; &#34;share,&#34; and &#34;sell&#34; have the same meaning as provided in the California Consumer Privacy Act of 2018 and the California Privacy Rights Act of 2020 &#40;collectively, &#34;CPRA&#34;&#41;.
        </Typography>
        <Typography
          nextElement='typography'
          component='h2'
          variant='h3'>
          California Privacy Policies
        </Typography>
        <Typography nextElement='typography'>
          California residents can find additional details about our privacy practices within the following privacy policies:
        </Typography>
        <BulletedList bulletedListData={privacyPoliciesBulletedListConfig} />
        <Accordion
          id='california-notice-at-collection-accordion'
          variant='secondary'
          toggleOpenIcon={PlusSignFillIcon}
          toggleCloseIcon={MinusSignFillIcon}
          items={[
            {
              id: 'ccpa-notice-at-collection',
              title: 'Notice at Collection',
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    The charts in the Information Collected section describe each category of Personal Information we may collect, along with examples of types of information falling into each of those categories when we collect Personal Information directly from you, including
                    when you visit our NorthwesternMutual.com website or other websites owned by the Northwestern Mutual Family of Companies. Please note that the charts will not include entries when our websites offer functionality that doesn&#39;t result in us collecting any Personal Information. Also, for additional information about all categories of Personal Information collected by Northwestern Mutual and our Northwestern Mutual Family of Companies, please read our <Link
                      id='ccpa-privacy-notices-link'
                      variant='primaryDeemphasize'
                      linkUrl='/privacy-notices/'>
                      Privacy Notices
                    </Link>.
                  </Typography>
                  <Typography
                    nextElement='typography'
                    component='h4'>
                    Sale and Sharing of Personal Information:
                  </Typography>
                  <Typography nextElement='typography'>
                    We do not sell your Personal Information to third parties, but we may share your Personal Information for our marketing
                    purposes. You have the right to opt out of this type of sharing by clicking on the &#34;Do Not Sell or Share My Personal Information&#34; link or by using an industry accepted opt&#45;out preference signal.
                  </Typography>
                  <TStyledBorder>
                    <TStyledHead>
                      <tr>
                        <TDStyledBold>
                          Category of Personal Information
                        </TDStyledBold>
                        <TDStyledBold>
                          Examples
                        </TDStyledBold>
                      </tr>
                    </TStyledHead>
                    <tbody>
                      <tr>
                        <TDStyled>
                          H. Internet or Network Activity
                        </TDStyled>
                        <TDStyled>
                          Browser or device information; browsing information&#59; IP address&#59; cookies, web beacons, pixel tags, and clickstream or other traffic data&#59; and interaction with our websites, mobile app, and advertisements
                        </TDStyled>
                      </tr>
                    </tbody>
                  </TStyledBorder>
                </>
              ),
            },
            {
              id: 'ccpa-information-collected',
              title: 'Information Collected',
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    Information Collected From You on All Pages Within NorthwesternMutual.com and Other Websites Owned by the Northwestern Mutual Family of Companies:
                  </Typography>
                  {/* note: should restructure all of these tables once we have standardized tables in AURA */}
                  <TStyledBorder>
                    <TStyledHead>
                      <tr>
                        <TDStyledBold>
                          Category of Personal Information
                        </TDStyledBold>
                        <TDStyledBold>
                          Examples
                        </TDStyledBold>
                      </tr>
                    </TStyledHead>
                    <tbody>
                      <tr>
                        <TDStyled>
                          H. Internet or Network Activity
                        </TDStyled>
                        <TDStyled>
                          Browser or device information&#59; browsing information&#59; IP address&#59; cookies, web beacons, pixel tags, and clickstream or other traffic data&#59; and interaction with our websites, mobile app, and advertisements
                        </TDStyled>
                      </tr>
                    </tbody>
                  </TStyledBorder>
                  <Typography nextElement='typography'>
                    Information Collected From You When You Fill Out Find a Financial Advisor Forms:
                  </Typography>
                  <TStyledBorder>
                    <TStyledHead>
                      <tr>
                        <TDStyledBold>
                          Category of Personal Information
                        </TDStyledBold>
                        <TDStyledBold>
                          Examples
                        </TDStyledBold>
                      </tr>
                    </TStyledHead>
                    <tbody>
                      <tr>
                        <TDStyled>
                          A. Personal Information and Identifiers
                        </TDStyled>
                        <TDStyled>
                          Name, email, phone number, zip code
                        </TDStyled>
                      </tr>
                      <tr>
                        <TDStyled>
                          B. Demographics
                        </TDStyled>
                        <TDStyled>
                          Age
                        </TDStyled>
                      </tr>
                      <tr>
                        <TDStyled>
                          C. Professional or Employment Information
                        </TDStyled>
                        <TDStyled>
                          Income or household income
                        </TDStyled>
                      </tr>
                      <tr>
                        <TDStyled>
                          H. Internet or Network Activity
                        </TDStyled>
                        <TDStyled>
                          Browser or device information&#59; browsing information&#59; IP address&#59; cookies, web beacons, pixel tags, and clickstream or other traffic data&#59; interaction with our websites and advertisements
                        </TDStyled>
                      </tr>
                      <tr>
                        <TDStyled>
                          J. Profile Information
                        </TDStyled>
                        <TDStyled>
                          Profiles reflecting a person&#39;s preferences, such as biggest or most important financial goal
                        </TDStyled>
                      </tr>
                    </tbody>
                  </TStyledBorder>
                  <Typography nextElement='typography'>
                    Information Collected From You When You Use the Retirement Calculator located on NorthwesternMutual.com:
                  </Typography>
                  <TStyledBorder>
                    <TStyledHead>
                      <tr>
                        <TDStyledBold>
                          Category of Personal Information
                        </TDStyledBold>
                        <TDStyledBold>
                          Examples
                        </TDStyledBold>
                      </tr>
                    </TStyledHead>
                    <tbody>
                      <tr>
                        <TDStyled>
                          A. Personal Information and Identifiers
                        </TDStyled>
                        <TDStyled>
                          Name, email, phone number, zip code
                        </TDStyled>
                      </tr>
                      <tr>
                        <TDStyled>
                          B. Demographics
                        </TDStyled>
                        <TDStyled>
                          Age, gender
                        </TDStyled>
                      </tr>
                      <tr>
                        <TDStyled>
                          C. Professional or Employment Information
                        </TDStyled>
                        <TDStyled>
                          Income or household income
                        </TDStyled>
                      </tr>
                      <tr>
                        <TDStyled>
                          E. Financial Information
                        </TDStyled>
                        <TDStyled>
                          Annual retirement contributions, total retirement savings
                        </TDStyled>
                      </tr>
                      <tr>
                        <TDStyled>
                          H. Internet or Network Activity
                        </TDStyled>
                        <TDStyled>
                          Browser or device information&#59; browsing information&#59; IP address&#59; cookies, web beacons, pixel tags, and clickstream or other traffic data&#59; interaction with our websites, mobile app, and our advertisements
                        </TDStyled>
                      </tr>
                      <tr>
                        <TDStyled>
                          J. Profile Information
                        </TDStyled>
                        <TDStyled>
                          Desired retirement age, intended retirement spending habits
                        </TDStyled>
                      </tr>
                    </tbody>
                  </TStyledBorder>
                  <Typography nextElement='typography'>
                    Information Collected From You When You Use the Life Insurance Calculator located on NorthwesternMutual.com:
                  </Typography>
                  <TStyledBorder>
                    <TStyledHead>
                      <tr>
                        <TDStyledBold>
                          Category of Personal Information
                        </TDStyledBold>
                        <TDStyledBold>
                          Examples
                        </TDStyledBold>
                      </tr>
                    </TStyledHead>
                    <tbody>
                      <tr>
                        <TDStyled>
                          A. Personal Information and Identifiers
                        </TDStyled>
                        <TDStyled>
                          Name, email, phone number, zip code
                        </TDStyled>
                      </tr>
                      <tr>
                        <TDStyled>
                          B. Demographics
                        </TDStyled>
                        <TDStyled>
                          Age, age of partner, number of children, age of youngest child, gender
                        </TDStyled>
                      </tr>
                      <tr>
                        <TDStyled>
                          C. Professional or Employment Information
                        </TDStyled>
                        <TDStyled>
                          Income, income of partner
                        </TDStyled>
                      </tr>
                      <tr>
                        <TDStyled>
                          E. Financial Information
                        </TDStyled>
                        <TDStyled>
                          Monthly expenses, mortgage amount, other debt, total assets
                        </TDStyled>
                      </tr>
                      <tr>
                        <TDStyled>
                          H. Internet or Network Activity
                        </TDStyled>
                        <TDStyled>
                          Browser or device information&#59; browsing information&#59; IP address&#59; cookies, web beacons, pixel tags, and clickstream or other traffic data&#59; interaction with our websites and advertisements
                        </TDStyled>
                      </tr>
                    </tbody>
                  </TStyledBorder>
                  <Typography nextElement='typography'>
                    Information Collected From You When You Contact Us on Innovation.NorthwesternMutual.com:
                  </Typography>
                  <TStyledBorder>
                    <TStyledHead>
                      <tr>
                        <TDStyledBold>
                          Category of Personal Information
                        </TDStyledBold>
                        <TDStyledBold>
                          Examples
                        </TDStyledBold>
                      </tr>
                    </TStyledHead>
                    <tbody>
                      <tr>
                        <TDStyled>
                          A. Personal Information and Identifiers
                        </TDStyled>
                        <TDStyled>
                          Name, email
                        </TDStyled>
                      </tr>
                      <tr>
                        <TDStyled>
                          H. Internet or Network Activity
                        </TDStyled>
                        <TDStyled>
                          Browser or device information&#59; browsing information&#59; IP address&#59; cookies, web beacons, pixel tags, and clickstream or other traffic data&#59; interaction with the our websites
                        </TDStyled>
                      </tr>
                      <tr>
                        <TDStyled>
                          J. Profile Information
                        </TDStyled>
                        <TDStyled>
                          Profile information about you that may be included with any attachments you submit
                        </TDStyled>
                      </tr>
                    </tbody>
                  </TStyledBorder>
                  <Typography nextElement='typography'>
                    Information Collected From You When You Attend a Recruiting Event, Job Fair or Visit Jobs.NorthwesternMutual.com:
                  </Typography>
                  <TStyledBorder>
                    <TStyledHead>
                      <tr>
                        <TDStyledBold>
                          Category of Personal Information
                        </TDStyledBold>
                        <TDStyledBold>
                          Examples
                        </TDStyledBold>
                      </tr>
                    </TStyledHead>
                    <tbody>
                      <tr>
                        <TDStyled>
                          A. Personal Information and Identifiers
                        </TDStyled>
                        <TDStyled>
                          Name, alias, date of birth, home address, driver&#39;s license number or state identification card, passport number, Social Security number, email address, contact information, physical or electronic signature
                        </TDStyled>
                      </tr>
                      <tr>
                        <TDStyled>
                          C. Professional or Employment Information
                        </TDStyled>
                        <TDStyled>
                          Salary, employment history, professional designations, veteran or military status, and other information collected from job applications or resumes
                        </TDStyled>
                      </tr>
                      <tr>
                        <TDStyled>
                          D. Education Information
                        </TDStyled>
                        <TDStyled>
                          School&#40;s&#41; attended, dates attended, degree&#40;s&#41; earned, academic achievements, transcripts and other information collected from job applications or resumes
                        </TDStyled>
                      </tr>
                      <tr>
                        <TDStyled>
                          H. Internet or Network Activity
                        </TDStyled>
                        <TDStyled>
                          Browser or device information&#59; browsing information&#59; IP address&#59; cookies, web beacons, pixel tags, and clickstream or other traffic data&#59; interaction with our websites
                        </TDStyled>
                      </tr>
                      <tr>
                        <TDStyled>
                          J. Profile Information
                        </TDStyled>
                        <TDStyled>
                          Profiles reflecting your preferences, such as interests, hobbies, characteristic tendencies, behaviors, attitudes, or aptitudes, including inferences drawn from Personal Information like browsing information and clickstream or other website traffic data
                        </TDStyled>
                      </tr>
                    </tbody>
                  </TStyledBorder>
                  <Typography nextElement='typography'>
                    Information Collected From You When You Contact Us to Make a Privacy Request:
                  </Typography>
                  <TStyledBorder>
                    <TStyledHead>
                      <tr>
                        <TDStyledBold>
                          Category of Personal Information
                        </TDStyledBold>
                        <TDStyledBold>
                          Examples
                        </TDStyledBold>
                      </tr>
                    </TStyledHead>
                    <tbody>
                      <tr>
                        <TDStyled>
                          A. Personal Information and Identifiers
                        </TDStyled>
                        <TDStyled>
                          Name, email, address, phone number, date of birth, last 4 digits of Social Security number
                        </TDStyled>
                      </tr>
                    </tbody>
                  </TStyledBorder>
                  <Typography nextElement='typography'>
                    Information Collected From You When You Contact Us to Make a Privacy Request as an Authorized Agent:
                  </Typography>
                  <TStyledBorder>
                    <TStyledHead>
                      <tr>
                        <TDStyledBold>
                          Category of Personal Information
                        </TDStyledBold>
                        <TDStyledBold>
                          Examples
                        </TDStyledBold>
                      </tr>
                    </TStyledHead>
                    <tbody>
                      <tr>
                        <TDStyled>
                          A. Personal Information and Identifiers
                        </TDStyled>
                        <TDStyled>
                          Name, email, address, phone number, date of birth, last 4 digits of Social Security number
                        </TDStyled>
                      </tr>
                    </tbody>
                  </TStyledBorder>
                </>
              ),
            },
            {
              id: 'ccpa-how-we-may-use-this-information',
              title: 'How We May Use This Information',
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    We may use Personal Information we collect from you for one or more of the following purposes:
                  </Typography>
                  <BulletedList bulletedListData={bulletedListConfig} />
                  <Typography nextElement='typography'>
                    For more detailed information about &#34;Purposes of Collection and Disclosure of Personal Information&#34; see our&nbsp;
                    <Link
                      id='ccpa-notice-at-collection-private-policy-third-link'
                      variant='primaryDeemphasize'
                      linkUrl='/ccpa-privacy-policy/'>
                      California Consumer Privacy Policy
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: 'ccpa-retention-of-personal-information',
              title: 'Retention of Personal Information',
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    We have policies and practices requiring the secure deletion of Personal Information when there is no applicable regulatory retention requirement and we no longer have a business need to use the information for a purpose that is compatible with our disclosed purposes of collection. Personal Information that has been aggregated or deidentified so that it cannot reasonably be used to infer information about you or otherwise be linked to you may be retained indefinitely.
                  </Typography>
                </>
              ),
            },
            {
              id: 'ccpa-changes-to-this-notice',
              title: 'Changes to this Notice',
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    We may change this Notice from time to time. When we make changes to this Notice, we will post the updated
                    Notice on the Privacy pages of our website and mobile app with a new &#34;Last Updated&#34; date. Any changes will
                    become effective when we post the updated Notice. Your use of the website or mobile app following these
                    changes means that you accept the updated Notice.
                  </Typography>
                </>
              ),
            },
            {
              id: 'ccpa-contact-information',
              title: 'Contact Information',
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    If you have any questions or comments about this Notice, the ways in which we collect and use your Personal
                    Information, your rights regarding such use, or if you wish to exercise your California privacy rights,
                    please do not hesitate to contact us by:
                  </Typography>
                  <BulletedList bulletedListData={contactInfoBulletedListConfig} />
                </>
              ),
            },
          ]}
        />
      </Col>
    </Row>
  </SectionContainer>
);
